import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { apiClient } from 'api-client';
import { AccessTime, DeleteOutlined, Done, AddOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import {
  areThereInvoices,
  budgetCostListIncludes,
  filterBudgetCostItems,
} from 'utils/functions/budgetCostsHelpers';
import { BUDGET_COST_NAME_MAX } from 'constants/fieldLimits';
import { DarkTooltip } from 'components/DarkTooltip';
import theme from 'theme';
import { InlineEdit } from 'components/InlineEdit';
import { useAuth } from 'auth';
import { InvoiceUpload } from './InvoiceUpload';
import { NotesEdit } from './NotesEdit';

const EnhancedTableHead = props => {
  const {
    onSelectAllClick,
    numSelected,
    rowCount,
    onDelete,
    onAddNewRow,
    status,
    currentBudgetCosts,
    canEdit,
  } = props;

  return (
    <TableHead>
      <TableRow>
        {status === 'approved' && canEdit && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              color="primary"
              onChange={onSelectAllClick}
            />
          </TableCell>
        )}

        <TableCell>
          {status === 'approved' && canEdit ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <IconButton
                onClick={onDelete}
                variant="outlined"
                disabled={numSelected === 0}
                sx={{
                  height: '2rem',
                  width: '2rem',
                  borderRadius: '3px',
                  marginRight: '1rem',
                  border:
                    numSelected === 0
                      ? '1px solid rgba(217, 72, 68, .5)'
                      : `1px solid ${theme.palette.border}`,
                  color: numSelected === 0 ? 'rgb(217, 72, 68)' : 'inherit',
                }}
                size="large"
              >
                <DeleteOutlined />
              </IconButton>
              <Typography variant="body2" sx={{ color: '#686868' }}>
                {numSelected} Selected
              </Typography>
            </Box>
          ) : (
            <div />
          )}
        </TableCell>

        <TableCell align="right" colSpan={3}>
          {status === 'approved' && canEdit ? (
            <Button
              onClick={onAddNewRow}
              variant="outlined"
              startIcon={<AddOutlined />}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                textTransform: 'none',
                height: '2rem',
              }}
              disabled={budgetCostListIncludes(currentBudgetCosts, '')}
            >
              Add budget cost
            </Button>
          ) : (
            <div />
          )}
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

const BudgetCosts = props => {
  const { enqueueSnackbar } = useSnackbar();
  const { budgetCosts, eduBudgetRequestId, eduBudgetRequestStatus } = props;
  const { isHardwareManager, getToken } = useAuth();
  const canEdit = !isHardwareManager;
  const [selected, setSelected] = useState([]);
  const [currentBudgetCosts, setCurrentBudgetCosts] = useState([]);
  const [newBudgetCostName, setNewBudgetCostName] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isSavingBudgetCost, setIsSavingBudgetCost] = useState(false);

  useEffect(() => {
    setCurrentBudgetCosts(budgetCosts);
  }, [setCurrentBudgetCosts, budgetCosts]);

  useEffect(() => {
    const items = filterBudgetCostItems(currentBudgetCosts);
    setFilteredItems(items);
  }, [currentBudgetCosts]);

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelecteds = currentBudgetCosts.map(n => n.budgetCost);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleDeleteSelected = async () => {
    const namesToDelete = [...selected];

    try {
      const result = await apiClient(
        'POST',
        `/requests/edu-budget/${eduBudgetRequestId}/budget-costs`,
        getToken,
        { action: 'delete', budgetCosts: namesToDelete },
      );
      if (result?.status !== 'OK') throw new Error(result?.message || 'Request failed');
      setSelected([]);
      setCurrentBudgetCosts(
        currentBudgetCosts.filter(cost => !namesToDelete.includes(cost.budgetCost)),
      );
    } catch (error) {
      console.error('Error deleting the budget cost', error);
      enqueueSnackbar('Error deleting the budget cost', { variant: 'error' });
    }
  };

  const handleAddNewRow = () => {
    const newBudgetCost = { budgetCost: '', amount: 0, invoiceIds: [] };
    setCurrentBudgetCosts(currentBudgetCosts.concat([newBudgetCost]));
  };

  const saveNewBudgetCost = async () => {
    if (isSavingBudgetCost) return;
    const isNameUnique = !budgetCostListIncludes(currentBudgetCosts, newBudgetCostName);
    if (newBudgetCostName === '' || !isNameUnique) return;

    const budgetCost = newBudgetCostName;
    setIsSavingBudgetCost(true);
    setNewBudgetCostName('');

    try {
      const result = await apiClient(
        'POST',
        `/requests/edu-budget/${eduBudgetRequestId}/budget-costs`,
        getToken,
        { action: 'add', budgetCost },
      );
      if (result?.status !== 'OK') throw new Error(result?.message || 'Request failed');
      setCurrentBudgetCosts(prev => prev.concat([{ budgetCost, amount: 0, invoiceIds: [] }]));
    } catch (error) {
      console.error('Error adding the budget cost', error);
      enqueueSnackbar('Error adding the budget cost', { variant: 'error' });
      setNewBudgetCostName(budgetCost);
    } finally {
      setIsSavingBudgetCost(false);
    }
  };

  const updateBudgetCostField = async (id, data) => {
    setCurrentBudgetCosts(
      currentBudgetCosts.map(cost => (cost.budgetCost === id ? { ...cost, ...data } : cost)),
    );

    const result = await apiClient(
      'POST',
      `/requests/edu-budget/${eduBudgetRequestId}/budget-costs`,
      getToken,
      { action: 'update', budgetCost: id, data },
    );
    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
  };

  const onEnter = event => {
    if (event.keyCode === 13) {
      saveNewBudgetCost();
    }
  };

  const isSelected = name => selected.indexOf(name) !== -1;

  if (!currentBudgetCosts.length) {
    return (
      eduBudgetRequestStatus === 'approved' && (
        <Box
          sx={{
            backgroundColor: '#F7F7F9',
            width: '100%',
            padding: '1rem 0',
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '0 1rem 1rem 1rem',
              borderBottom: `1px solid ${theme.palette.border}`,
            }}
          >
            {eduBudgetRequestStatus === 'approved' && canEdit ? (
              <Button
                onClick={handleAddNewRow}
                variant="outlined"
                startIcon={<AddOutlined />}
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  textTransform: 'none',
                  height: '2rem',
                }}
                disabled={budgetCostListIncludes(currentBudgetCosts, '')}
              >
                Add budget cost
              </Button>
            ) : (
              <div />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: ' center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '1rem 0',
            }}
          >
            <Typography
              sx={{ fontSize: '18px', fontWeight: '600', marginTop: '1rem', color: 'black' }}
            >
              There are no budget cost details yet
            </Typography>
            <Typography variant="subtitle1">
              Added invoices and payment details will appear here
            </Typography>
          </Box>
        </Box>
      )
    );
  }
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ color: 'black' }}>
        Invoices
      </Typography>
      <Paper sx={{ boxShadow: 'none' }}>
        <TableContainer sx={{ marginBottom: '2rem', marginLeft: '0.5rem', marginTop: '2rem' }}>
          <Table aria-labelledby="tableTitle" aria-label="enhanced table">
            <EnhancedTableHead
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
              rowCount={currentBudgetCosts.length}
              onDelete={handleDeleteSelected}
              onAddNewRow={handleAddNewRow}
              status={eduBudgetRequestStatus}
              currentBudgetCosts={currentBudgetCosts}
              canEdit={canEdit}
            />
            <TableHead
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 300,
                },
              }}
            >
              <TableRow>
                {eduBudgetRequestStatus === 'approved' && canEdit && (
                  <TableCell sx={{ verticalAlign: 'top' }} />
                )}
                <TableCell sx={{ verticalAlign: 'top' }}>Budget costs</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>Amount (EUR)</TableCell>
                <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                  Notes
                </TableCell>
                {(eduBudgetRequestStatus === 'approved' ||
                  areThereInvoices(currentBudgetCosts)) && (
                  <TableCell sx={{ verticalAlign: 'top' }} align="left" width="338px">
                    <DarkTooltip title="Attached files: invoices, tickets, bills, etc.">
                      <span>Attachment</span>
                    </DarkTooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentBudgetCosts.map((row, index) => {
                const isItemSelected = isSelected(row.budgetCost);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow tabIndex={-1} key={row.budgetCost} selected={isItemSelected}>
                    {eduBudgetRequestStatus === 'approved' && canEdit && (
                      <TableCell
                        sx={{ verticalAlign: 'top', paddingTop: '0.25rem' }}
                        padding="checkbox"
                      >
                        <Checkbox
                          role="checkbox"
                          color="primary"
                          aria-checked={isItemSelected}
                          onClick={event => handleClick(event, row.budgetCost)}
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                    )}

                    <TableCell
                      sx={{ verticalAlign: 'top' }}
                      align="left"
                      component="th"
                      id={labelId}
                      scope="row"
                    >
                      {row.budgetCost === '' && (
                        <Box sx={{ marginTop: '-0.75rem' }}>
                          <Autocomplete
                            id="new-budget-cost"
                            freeSolo
                            disabled={isSavingBudgetCost}
                            options={filteredItems}
                            onChange={(e, data) => {
                              setNewBudgetCostName(data);
                            }}
                            onInputChange={(e, data) => {
                              setNewBudgetCostName(data);
                            }}
                            onBlur={() => saveNewBudgetCost()}
                            onKeyDown={onEnter}
                            renderInput={props => (
                              <TextField
                                {...props}
                                placeholder="Purpose of payment"
                                inputProps={{
                                  ...props.inputProps,
                                  maxLength: BUDGET_COST_NAME_MAX,
                                }}
                                error={
                                  newBudgetCostName !== '' &&
                                  budgetCostListIncludes(currentBudgetCosts, newBudgetCostName)
                                }
                                helperText={
                                  newBudgetCostName !== '' &&
                                  budgetCostListIncludes(currentBudgetCosts, newBudgetCostName) &&
                                  'Enter unique budget cost name for this request'
                                }
                                variant="standard"
                              />
                            )}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'end',
                              fontWeight: 300,
                              fontSize: '10px',
                              lineHeight: '12px',
                              marginTop: '0.25rem',
                            }}
                          >
                            {newBudgetCostName.length}/{BUDGET_COST_NAME_MAX}
                            {newBudgetCostName.length >= BUDGET_COST_NAME_MAX && (
                              <Box component="span" sx={{ color: 'error.main', marginLeft: '8px' }}>
                                Maximum length reached
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                      {row.budgetCost === 'Enrolment fee' ? (
                        <div>
                          {row.budgetCost} <span>(Final amount may include tax/VAT)</span>
                        </div>
                      ) : (
                        <div>{row.budgetCost} </div>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ verticalAlign: 'top' }}
                      align="left"
                      component="th"
                      id={labelId}
                      scope="row"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(eduBudgetRequestStatus === 'pending' ||
                          eduBudgetRequestStatus === 'approved') && (
                          <DarkTooltip
                            title="Preliminary amount - may differ until the 
              request is completed"
                          >
                            <AccessTime
                              sx={{
                                marginRight: '0.5rem',
                                color: '#686868',
                                width: '1.2rem',
                                height: '1.2rem',
                              }}
                            />
                          </DarkTooltip>
                        )}
                        {eduBudgetRequestStatus === 'completed' && (
                          <Done
                            sx={{
                              color: '#65B32E',
                              marginRight: '0.5rem',
                              width: '1.2rem',
                              height: '1.2rem',
                            }}
                          />
                        )}

                        {(eduBudgetRequestStatus === 'pending' ||
                          eduBudgetRequestStatus === 'approved') &&
                        canEdit ? (
                          <InlineEdit
                            id={row.budgetCost}
                            name="amount"
                            value={row.amount || 0}
                            onUpdate={updateBudgetCostField}
                            tooltipTitle="Click to edit."
                            min={0}
                          />
                        ) : (
                          row.amount
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ verticalAlign: 'top' }}
                      align="left"
                      component="th"
                      id={labelId}
                      scope="row"
                    >
                      <NotesEdit
                        id={row.budgetCost}
                        name="notes"
                        value={row.notes || ''}
                        onUpdate={updateBudgetCostField}
                        status={eduBudgetRequestStatus}
                        editable={canEdit}
                      />
                    </TableCell>
                    {(eduBudgetRequestStatus === 'approved' ||
                      areThereInvoices(currentBudgetCosts)) && (
                      <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                        <InvoiceUpload
                          budgetCostName={row.budgetCost}
                          eduBudgetRequestId={eduBudgetRequestId}
                          status={eduBudgetRequestStatus}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BudgetCosts;
